import { Directive } from '@angular/core';
import { AbstractControlOptions, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';

@Directive()
export abstract class EaSimulationScpiFormBuilder extends PrFormAbstract {
  generate(entity: any) {
    let options: AbstractControlOptions = {};
    return this.formBuilder.group(
      {
        investedAmount: [
          entity ? Math.round(entity.investedAmount * 100) / 100 : null,
          Validators.required,
        ],
        projectDuration: [
          entity ? entity.projectDuration / 12 : null,
          Validators.required,
        ],
        dismembermentDuration: [
          entity && entity.dismembermentDuration
            ? entity.dismembermentDuration
            : 10,
          this.prFormService.AddValidatorsBasedOnCondition(
            this.prFormService
              .isTargetControlValueEqualTo('ownership', 'BARE_OWNERSHIP')
              .bind(this),
            Validators.required,
          ),
        ],
        ownership: [
          entity ? entity.ownership : null,
          [
            Validators.required,
            this.prFormService.RefreshEveryValidators(
              'cashOrCredit, dismembermentDuration',
            ),
          ],
        ],
        partRevalorisationRate: [
          entity ? entity.partRevalorisationRate * 100 : null,
          Validators.required,
        ],
        rentalReversionRate: [
          entity ? entity.rentalReversionRate * 100 : null,
          Validators.required,
        ],

        // Versement programmé global (€/mois) + réinvestissement des dividendes : réservés à
        // la pleine propriété, réparti côté back au prorata sur les lignes éligibles. Facultatifs,
        // donc sans validateur. Voir la synchro bidirectionnelle dans SimulationScpiComponent.
        globalProgrammedAmount: [
          entity && entity.globalProgrammedAmount != null
            ? entity.globalProgrammedAmount
            : null,
        ],
        globalDividendReinvestment: [
          entity ? !!entity.globalDividendReinvestment : false,
        ],

        cashOrCredit: [
          entity ? entity.cashOrCredit : null,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('ownership', 'FULL_OWNERSHIP')
                .bind(this),
              Validators.required,
            ),
            this.prFormService.RefreshEveryValidators([
              'loanDuration',
              'loanRate',
            ]),
          ],
        ],
        loanDuration: [
          entity ? entity.loanDuration : null,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('cashOrCredit', 'CREDIT')
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        loanRate: [
          entity ? entity.loanRate || 4 : 4,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('cashOrCredit', 'CREDIT')
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        loanInsuranceRate: [
          entity ? entity.loanInsuranceRate || 0.3 : 0.3,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('cashOrCredit', 'CREDIT')
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        bankingFees: [
          entity ? entity.bankingFees || 0 : 0,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('cashOrCredit', 'CREDIT')
                .bind(this),
              Validators.required,
            ),
          ],
        ],
        franchiseTotale: [
          entity ? entity.franchiseTotale || 0 : 0,
          [
            this.prFormService.AddValidatorsBasedOnCondition(
              this.prFormService
                .isTargetControlValueEqualTo('cashOrCredit', 'CREDIT')
                .bind(this),
              Validators.required,
            ),
          ],
        ],
      },
      options,
    );
  }

  extract() {
    // Les paramètres globaux versement/réinvestissement n'ont de sens qu'en pleine propriété
    // (le back les ignore en démembrement). On les remonte à null/false hors pleine propriété
    // pour ne rien envoyer d'incohérent.
    const isFullOwnership = this.form.value.ownership === 'FULL_OWNERSHIP';
    // Le champ est un <input type="text"> : sa valeur est une chaîne. On la coerce en nombre
    // (comme les autres champs numériques) pour que la comparaison "montant global modifié ?"
    // côté parent ne compare pas une chaîne à un nombre (sinon elle serait toujours vraie et
    // effacerait à tort la répartition manuelle par SCPI).
    const rawGlobalProgrammed = this.emptyIsNull(
      this.form.value.globalProgrammedAmount,
    );
    const globalProgrammedAmount =
      isFullOwnership &&
      rawGlobalProgrammed != null &&
      Number.isFinite(Number(rawGlobalProgrammed))
        ? Number(rawGlobalProgrammed)
        : null;
    let dto: any = {
      id: null,
      investedAmount: this.emptyIsNull(this.form.value.investedAmount),
      projectDuration: this.emptyIsNull(this.form.value.projectDuration * 12),
      dismembermentDuration: this.emptyIsNull(
        +this.form.value.dismembermentDuration,
      ),
      ownership: this.emptyIsNull(this.form.value.ownership),
      partRevalorisationRate: this.emptyIsNull(
        this.form.value.partRevalorisationRate / 100,
      ),
      rentalReversionRate: this.emptyIsNull(
        this.form.value.rentalReversionRate / 100,
      ),

      globalProgrammedAmount: globalProgrammedAmount,
      globalDividendReinvestment: isFullOwnership
        ? !!this.form.value.globalDividendReinvestment
        : false,

      cashOrCredit: this.emptyIsNull(this.form.value.cashOrCredit),
      loanDuration: this.emptyIsNull(+this.form.value.loanDuration),
      loanRate: this.emptyIsNull(this.parseFloat(this.form.value.loanRate)),
      loanInsuranceRate: this.emptyIsNull(
        this.parseFloat(this.form.value.loanInsuranceRate),
      ),
      bankingFees: this.emptyIsNull(
        this.parseFloat(this.form.value.bankingFees),
      ),
      franchiseTotale: this.emptyIsNull(+this.form.value.franchiseTotale),
    };
    return dto;
  }
}
